import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useEffect, useState } from 'react';

import Logo from 'assets/images/branding/logo.svg';
import { DailyExportData, useGetDailyExportDataQuery } from 'store/services/PDFService';
import { Timeline } from 'pages/Core';

interface FullScreenLoaderProps {
	isLoading?: boolean;
	progress: number;
	message: string;
}

const FullScreenLoader = ({
	isLoading = false,
	progress = 0,
	message = 'Getting your PDFs ready...',
}: FullScreenLoaderProps) => {
	if (!isLoading && progress === 100) return null;

	const width = `${Math.min(progress, 100)}%`;

	return (
		<div className='fixed inset-0 flex flex-col items-center justify-center bg-white z-50'>
			<div className='flex justify-start w-96 ml-4 items-center'>
				<img className='h-8 w-8 relative -left-[2px] mb-1' src={Logo} alt='Merlin' />
				<div className='m-0 text-h6 text-gray-600 ml-3'>Merlin - Daily OR Scorecard</div>
			</div>
			<div className='relative w-96 h-4 rounded-full bg-gray-200 overflow-hidden'>
				<div
					className={
						width === '100%'
							? `absolute top-0 left-0 h-full bg-green-500 transition-all duration-1000 ease-in-out `
							: `absolute top-0 left-0 h-full bg-blue-500 transition-all duration-1000 ease-in-out `
					}
					style={{ width }}
				/>
			</div>
			<div className='text-gray-300 text-left mt-2 animate-pulse w-96 pl-1 text-p2'>{message}</div>
		</div>
	);
};

export function DailyExport() {
	const token = new URLSearchParams(window.location.search).get('token');
	const [currentPage, setCurrentPage] = useState<DailyExportData>();
	const [loadingProgress, setLoadingProgress] = useState<{ current: number; total: number }>({
		current: 0,
		total: 0,
	});

	const { data, isLoading } = useGetDailyExportDataQuery(
		{
			token: token ?? '',
		},
		{
			skip: !token,
		}
	);

	const [victoryChartEl, setVictoryChartEl] = useState<HTMLElement | null>(null);

	// capture chart after it renders
	useEffect(() => {
		const el = document.getElementById('victory-chart');
		if (el) setVictoryChartEl(el);
	}, []);

	const captureAndAddPage = async (doc: jsPDF, elementId: string) => {
		const el = document.getElementById(elementId);
		if (!el) return;

		const html2canvasOptions = {
			useCORS: true,
			allowTaint: true,
			dpi: 500,
			scrollY: -window.scrollY,
		};

		const canvas = await html2canvas(el, html2canvasOptions);
		const imageData = canvas.toDataURL('image/jpeg');
		doc.addImage(imageData, 'JPEG', 10, 10, 190, 0);
	};

	useEffect(() => {
		if (!data?.responses?.length || !victoryChartEl) return;

		const generateSinglePDF = async () => {
			setLoadingProgress({
				total: data.responses.length,
				current: 0,
			});

			const doc = new jsPDF();

			for (let i = 0; i < data.responses.length; i++) {
				const item = data.responses[i];

				setCurrentPage(item);

				// wait for timeline to draw
				await new Promise((resolve) => setTimeout(resolve, 1000));

				await captureAndAddPage(doc, 'pdf-container');

				// If this isn't a last item, add another page for the next item
				if (i < data.responses.length - 1) {
					doc.addPage();
				}

				setLoadingProgress((prev) => ({ ...prev, current: prev.current + 1 }));
			}

			// add final description page
			doc.addPage();
			await captureAndAddPage(doc, 'desc-container');

			doc.save(`Daily OR Scorecards ${data?.responses[0].date}.pdf`);

			window.close();
		};

		generateSinglePDF();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, victoryChartEl]);

	return (
		<>
			<FullScreenLoader
				isLoading={!isLoading}
				progress={(loadingProgress.current / loadingProgress.total) * 100 || 0}
				message={currentPage ? `Generating ${currentPage.name} scorecard...` : 'Getting your PDFs ready...'}
			/>

			<div id={`pdf-container`} className='h-[1920px] w-[1080px] overflow-hidden'>
				<div className='flex justify-between content-center'>
					<div className='flex text-center'>
						<div className=''>
							<img className='h-12 w-16 relative -left-[2px]' src={Logo} alt='Merlin' />
						</div>
						<p className='pl-4 whitespace-nowrap text-h4 font-semibold content-center mb-2'>Daily OR Scorecard</p>
					</div>
					<div className='flex flex-col text-right'>
						<div className='text-h5 font-semibold'>{currentPage?.name}</div>
						<div>{currentPage?.healthsystem_name}</div>
						<div>{currentPage?.date}</div>
					</div>
				</div>

				<div className='w-full bg-blue-50 p-5 flex flex-wrap justify-around rounded-md border border-blue-500 mt-5'>
					<div className='bg-white p-2 border border-blue-400 m-2 w-1/4 px-5 rounded-sm'>
						<div className='flex flex-col justify-between'>
							<div className='flex justify-between'>
								<div className='text-gray-500 text-p3'>
									{currentPage?.prime_time_start} to {currentPage?.prime_time_end}
								</div>
								<div className='text-gray-500 text-p3'>{currentPage?.rooms_count} Rooms</div>
							</div>
							<p className='text-h2 font-bold'>{currentPage?.primetime_utilization}%</p>

							<div>
								<p className='text-gray-500 text-p3 pb-1'>Primetime Utilization</p>
							</div>
						</div>
					</div>
					<div className='bg-white p-2 border border-blue-400 m-2 w-1/4 px-5 rounded-sm'>
						<div className='flex flex-col justify-between h-full'>
							<p className='text-h2 font-bold'>{currentPage?.avg_out_in_wheels_turnover} min</p>
							<div>
								<p className='text-gray-500 text-p3 pb-1'>Avg Wheels Out to In Turnover</p>
							</div>
						</div>
					</div>
					<div className='bg-white p-2 border border-blue-400 m-2 w-1/4 px-5 rounded-sm'>
						<div className='flex flex-col justify-between h-full'>
							<p className='text-h2 font-bold'>{currentPage?.fcots_start_percentage}%</p>
							<div>
								<p className='text-gray-500 text-p3 pb-1'>First Case On Time Start %</p>
							</div>
						</div>
					</div>
					<div className='bg-white p-2 border border-blue-400 m-2 w-1/4 px-5 rounded-sm'>
						<div className='flex flex-col justify-between h-full'>
							<p className='text-h2 font-bold'>{currentPage?.case_volume ?? 0}</p>
							<div>
								<p className='text-gray-500 text-p3 pb-1'>Completed Cases/Volume</p>
							</div>
						</div>
					</div>

					<div className='bg-white p-2 border border-blue-400 m-2 w-1/4 px-5 rounded-sm'>
						<div className='flex flex-col justify-between h-full'>
							<p
								className={
									currentPage?.scheduled_cases !== undefined && currentPage.scheduled_cases > -1
										? 'text-h2 font-bold'
										: 'text-gray-500 text-p3 py-3 font-bold text-transform: uppercase'
								}
							>
								{currentPage?.scheduled_cases !== undefined && currentPage.scheduled_cases > -1
									? currentPage.scheduled_cases
									: 'needs additional data source'}
							</p>
							<div>
								<p className='text-gray-500 text-p3 pb-1'>Scheduled Cases</p>
							</div>
						</div>
					</div>
					<div className='bg-white p-2 border border-blue-400 m-2 w-1/4 px-5 rounded-sm'>
						<div className='flex flex-col justify-between h-full'>
							<p
								className={
									currentPage?.same_day_canceled_cases !== undefined && currentPage.same_day_canceled_cases > -1
										? 'text-h2 font-bold'
										: 'text-gray-500 text-p3 py-3 font-bold text-transform: uppercase'
								}
							>
								{currentPage?.same_day_canceled_cases !== undefined && currentPage.same_day_canceled_cases > -1
									? currentPage.same_day_canceled_cases
									: 'needs additional data source'}
							</p>
							<div>
								<p className='text-gray-500 text-p3 pb-1'>Same Day Cancellations</p>
							</div>
						</div>
					</div>

					<div className='bg-white p-2 border border-blue-400 m-2 w-1/4 px-5 rounded-sm'>
						<div className='flex flex-col justify-between h-full'>
							<p className='text-h2 font-bold'>{currentPage?.avg_delay_first_case} min</p>
							<div>
								<p className='text-gray-500 text-p3 pb-1'>Avg Delay Minutes for First Cases</p>
							</div>
						</div>
					</div>
					<div className='bg-white p-2 border border-blue-400 m-2 w-1/4 px-5 rounded-sm'>
						<div className='flex flex-col justify-between h-full'>
							<p className='text-h2 font-bold'>{currentPage?.avg_procedure_stop_start_turnover} min</p>
							<div>
								<p className='text-gray-500 text-p3 pb-1'>Avg Procedure Stop to Start Turnover</p>
							</div>
						</div>
					</div>

					<div className='bg-white p-2 border border-blue-400 m-2 w-1/4 px-5 rounded-sm'>
						<div className='flex flex-col justify-between h-full'>
							<p className='text-h2 font-bold'>{currentPage?.number_of_addons}</p>
							<div>
								<p className='text-gray-500 text-p3 pb-1'>Number of Add-Ons</p>
							</div>
						</div>
					</div>
				</div>

				<div id='victory-chart'>
					{currentPage?.case_volume ? (
						<>
							<Timeline
								hideDatePicker={true}
								heightMultiple={12}
								truncateAxisLabel={true}
								tickerTransform={'translate(0, 4)'}
								stackBarWidth={8.0}
								hideFilters={true}
								maxRoomsDisplayed={29}
								labelMaxLength={14}
								parentProvidedData={currentPage?.timeline}
								hideZoomPicker={true}
							/>
							<div className='text-center text-blue-700 mt-7'>More information available in Merlin</div>
						</>
					) : (
						<div className='text-center text-blue-700 mt-14 uppercase font-bold'>No cases to show</div>
					)}
				</div>
			</div>

			<div id='desc-container' className='h-[1920px] w-[1080px] overflow-hidden'>
				<div className='w-full bg-blue-50 p-5 flex flex-col justify-between rounded-md border border-blue-500 mt-5 gap-5'>
					<div className='flex flex-col'>
						<div className='font-semibold text-blue-500 whitespace-nowrap'>Primetime Utilization</div>
						<div className='text-black'>
							The percentage of time the operating room is used during its designated “primetime” hours, typically the busiest
							and most productive hours of the day. <br />
							<strong>Example Calculation:</strong> If the OR is used for 6 hours during an 8-hour primetime period, the
							utilization is (6/8) * 100 = 75%.
						</div>
					</div>
					<div className='flex flex-col'>
						<div className='font-semibold text-blue-500 whitespace-nowrap'>Avg Wheels Out to In Turnover</div>
						<div className='text-black'>
							The average time it takes to prepare the operating room for the next patient after the previous patient has been
							wheeled out. It includes cleaning and setting up for the next procedure. <br />
							<strong>Example Calculation:</strong> If the turnover times for three cases are 8, 10, and 12 minutes, the
							average turnover time is (8 + 10 + 12) / 3 = 10 minutes.
						</div>
					</div>
					<div className='flex flex-col'>
						<div className='font-semibold text-blue-500 whitespace-nowrap'>First Case On Time Start %</div>
						<div className='text-black'>
							The percentage of first surgeries of the day that start on time, as scheduled. <br />
							<strong>Example Calculation:</strong> If 17 out of 20 first cases start on time, the percentage is (17/20) * 100
							= 85%.
						</div>
					</div>
					<div className='flex flex-col'>
						<div className='font-semibold text-blue-500 whitespace-nowrap'>Completed Cases/Volume</div>
						<div className='text-black'>
							The total number of surgeries or procedures that were completed. <br />
							<strong>Example Calculation:</strong> If 343 surgeries were completed, the volume is 343.
						</div>
					</div>
					<div className='flex flex-col'>
						<div className='font-semibold text-blue-500 whitespace-nowrap'>Scheduled Cases</div>
						<div className='text-black'>
							The total number of surgeries or procedures that were scheduled to take place. <br />
							<strong>Example Calculation:</strong> If 343 surgeries were scheduled, the number of scheduled cases is 343.
						</div>
					</div>
					<div className='flex flex-col'>
						<div className='font-semibold text-blue-500 whitespace-nowrap'>Same Day Cancellations</div>
						<div className='text-black'>
							The number of surgeries or procedures that were canceled on the same day they were scheduled to occur. <br />
							<strong>Example Calculation:</strong> If 36 surgeries were canceled on the same day, the number of same day
							cancellations is 36.
						</div>
					</div>
					<div className='flex flex-col'>
						<div className='font-semibold text-blue-500 whitespace-nowrap'>Avg Delay Minutes for First Cases</div>
						<div className='text-black'>
							The average delay time for the first surgeries of the day, indicating how much later they started compared to the
							scheduled time. <br />
							<strong>Example Calculation:</strong> If the delays for three first cases are 10, 12, and 14 minutes, the average
							delay is (10 + 12 + 14) / 3 = 12 minutes.
						</div>
					</div>
					<div className='flex flex-col'>
						<div className='font-semibold text-blue-500 whitespace-nowrap'>Avg Procedure Stop to Start Turnover</div>
						<div className='text-black'>
							The average time between the end of one procedure and the start of the next one, including cleaning and
							preparation. <br />
							<strong>Example Calculation:</strong> If the turnover times between procedures are 14, 15, and 16 minutes, the
							average turnover time is (14 + 15 + 16) / 3 = 15 minutes.
						</div>
					</div>
					<div className='flex flex-col'>
						<div className='font-semibold text-blue-500 whitespace-nowrap'>Number of Add-Ons</div>
						<div className='text-black'>
							The number of additional surgeries or procedures that were added to the schedule after the initial planning.{' '}
							<br />
							<strong>Example Calculation:</strong> If 15 additional surgeries were added, the number of add-ons is 15.
						</div>
					</div>
				</div>
			</div>
		</>
	);
}

export default DailyExport;
